/**
 * Merge tokens — plan §6.1 (the Tokens guard) and §15 (the task view).
 *
 * `{{first_name}}` in a draft or a template resolves from the contact,
 * account and sender at SEND time. One registry, one merge implementation,
 * three consumers that must never disagree:
 *
 *   - the Tokens guard refuses a draft using a token whose backing field is
 *     empty for this recipient (unless the token carries a fallback),
 *   - the send path performs the merge on the exact bytes that go out,
 *   - the preview endpoint performs the SAME merge, so what the rep sees is
 *     what the prospect receives.
 *
 * Fallback syntax: `{{first_name|there}}` renders the value, or "there" when
 * the field is empty — the pattern every mail-merge user already knows.
 */

import { humaniseIndustry } from '../prospecting/industries';

export interface MergeTokenDef {
  name: string;
  label: string;
  source: 'contact' | 'account' | 'sender' | 'step';
  /**
   * MINTED, not read: the value does not exist until something creates it at
   * preview or send time — a vidshare share link, a booking link row.
   *
   * The consequence that matters is that these must never carry a fallback.
   * `{{first_name|there}}` is the author saying what to do when a field is
   * empty; `{{booking_link|}}` is an email that promises a meeting and links
   * to nothing. The picker therefore does not offer a fallback slot for these,
   * and their absence is a refusal to send rather than a blank.
   */
  minted?: true;
  /**
   * Resolves to nothing for most recipients BY DESIGN — the Article 14 notice
   * is owed only where the data's source and jurisdiction say so — and the
   * merge treats an empty value as unresolved unless the token carries a
   * fallback. So an optional token is always written with the empty
   * fallback, `{{art14_block|}}`: the picker inserts that form, the block
   * lint requires it, and a bare `{{art14_block}}` is a refusal to send to
   * everyone who is owed nothing.
   */
  optional?: true;
}

export const MERGE_TOKENS: readonly MergeTokenDef[] = [
  { name: 'first_name', label: 'First name', source: 'contact' },
  { name: 'last_name', label: 'Last name', source: 'contact' },
  { name: 'full_name', label: 'Full name', source: 'contact' },
  { name: 'title', label: 'Job title', source: 'contact' },
  { name: 'email', label: 'Email address', source: 'contact' },
  { name: 'country', label: 'Country code', source: 'contact' },
  { name: 'company', label: 'Company name', source: 'account' },
  { name: 'company_domain', label: 'Company domain', source: 'account' },
  { name: 'industry', label: 'Industry', source: 'account' },
  { name: 'company_size', label: 'Company size', source: 'account' },
  { name: 'company_country', label: 'Company country code', source: 'account' },
  { name: 'sender_name', label: 'Sender name', source: 'sender' },
  { name: 'sender_email', label: 'Sender email', source: 'sender' },
  /**
   * The sender's booking page, with this recipient's attribution attached.
   *
   * `sender` rather than `contact`, because it is the REP's link — but like
   * the video tokens it is minted rather than read, so `tokenValuesFrom` does
   * not produce it. `send/booking-link.ts` mints it at send time (one row per
   * task, so a retry does not mint a second) and the preview injects the bare
   * link, which is the same page without the attribution a preview must not
   * write. A rep with no slug or no live profile resolves it to nothing, and
   * the draft refuses to send rather than offering a dead page.
   */
  { name: 'booking_link', label: 'Booking link', source: 'sender', minted: true },
  /**
   * The video tokens resolve ONLY when a video is attached — by the sequence
   * step (channel_config.video) or by the rep for one task (video_override).
   * All three are per-recipient, minted by the PMG Video Tool at
   * selection/preview/send time (idempotent per video+email, so every call
   * converges on one link). `tokenValuesFrom` deliberately does not produce
   * them — the send context and the guard runner inject them when, and only
   * when, a video is attached.
   *
   *   video_embed — the whole email block: clickable thumbnail image linking
   *                 to the video (falls back to a styled text link when the
   *                 video has no thumbnail). What the picker inserts.
   *   video_link  — just the per-recipient share URL.
   *   video_thumb — just the stable thumbnail image URL. Unlike the other
   *                 two it stays unresolved when the video has no thumbnail,
   *                 so a draft using it directly refuses to send half-broken.
   */
  { name: 'video_embed', label: 'Video (thumbnail + link)', source: 'step', minted: true },
  { name: 'video_link', label: 'Video link', source: 'step', minted: true },
  { name: 'video_thumb', label: 'Video thumbnail URL', source: 'step', minted: true },
] as const;

/**
 * The footer tokens a MARKETING template may carry — Block 0.3 of the
 * marketing plan, decision P7. The footer is content: a template's footer
 * section places these, the send consumer resolves them before the merge,
 * and compose appends only what the body did not already carry. A separate
 * list, never folded into `MERGE_TOKENS`: the SDR picker must not offer an
 * unsubscribe link, because one on 1:1 mail is the tell that it was not
 * (§7.2).
 */
export const MARKETING_TOKEN = {
  unsubscribeUrl: 'unsubscribe_url',
  postalAddress: 'postal_address',
  art14Block: 'art14_block',
} as const;

export const MARKETING_TOKENS: readonly MergeTokenDef[] = [
  { name: MARKETING_TOKEN.unsubscribeUrl, label: 'Unsubscribe link', source: 'sender', minted: true },
  { name: MARKETING_TOKEN.postalAddress, label: 'Postal address', source: 'sender', minted: true },
  {
    name: MARKETING_TOKEN.art14Block,
    label: 'Article 14 notice, when owed',
    source: 'sender',
    optional: true,
  },
];

export type TokenValues = Record<string, string | null>;

/** `{{name}}` or `{{name|fallback}}`. The same shape the Tokens guard parses. */
export const MERGE_TOKEN_PATTERN = /\{\{\s*([a-z0-9_.]+)\s*(?:\|([^}]*))?\}\}/gi;

export interface MergeResult {
  text: string;
  /** Tokens that could not be resolved — unknown, or empty with no fallback. */
  unresolved: string[];
  /** Every token the text referenced, resolved or not. */
  used: string[];
}

/**
 * Resolve every merge token in `text` against `values`.
 *
 * An unresolvable token is left VERBATIM in the output and reported — never
 * silently replaced with an empty string, because "Hi ," is a worse failure
 * than "Hi {{first_name}},": the caller must treat a non-empty `unresolved`
 * as a refusal to send.
 */
export function mergeTokens(text: string, values: TokenValues): MergeResult {
  const unresolved: string[] = [];
  const used: string[] = [];

  const out = text.replace(MERGE_TOKEN_PATTERN, (whole, rawName: string, fallback?: string) => {
    const name = rawName.toLowerCase();
    used.push(name);

    if (!(name in values)) {
      unresolved.push(name);
      return whole;
    }

    const value = values[name];
    if (value !== null && value !== undefined && value.trim() !== '') return value.trim();
    if (fallback !== undefined) return fallback.trim();

    unresolved.push(name);
    return whole;
  });

  return { text: out, unresolved, used };
}

/**
 * The token values for one recipient, from the row shape both the JIT loader
 * and the send context produce. Missing fields resolve to null, which the
 * guard reports and the merge refuses — never to an invented value.
 */
export function tokenValuesFrom(fields: {
  firstName?: string | null;
  lastName?: string | null;
  title?: string | null;
  email?: string | null;
  countryCode?: string | null;
  company?: string | null;
  companyDomain?: string | null;
  industry?: string | null;
  companySize?: string | null;
  companyCountry?: string | null;
  senderName?: string | null;
  senderEmail?: string | null;
}): TokenValues {
  const fullName = [fields.firstName, fields.lastName].filter(Boolean).join(' ');
  return {
    first_name: fields.firstName ?? null,
    last_name: fields.lastName ?? null,
    full_name: fullName || null,
    title: fields.title ?? null,
    email: fields.email ?? null,
    country: fields.countryCode ?? null,
    company: fields.company ?? null,
    company_domain: fields.companyDomain ?? null,
    // Callers pass the canonical name where there is one; whatever else arrives is read as words, never as an enum key.
    industry: fields.industry ? humaniseIndustry(fields.industry) : null,
    company_size: fields.companySize ?? null,
    company_country: fields.companyCountry ?? null,
    sender_name: fields.senderName ?? null,
    sender_email: fields.senderEmail ?? null,
  };
}
